import { useEffect, useRef, useState } from 'react'
import {Download, FileText, Loader2} from 'lucide-react'
import QRCode from 'qrcode'
import { jsPDF } from 'jspdf'
import toast from 'react-hot-toast'
import { carregarImagem } from '../utils/carregarTemplate'

interface FolderPersonalizadoProps {
  usuarioConsultora: string
}

const FOLDER_TEMPLATE_URL =
  'https://static.lumi.new/6f/6fc8d8dbb89cadce82e9d918a462717f.webp'

// Posicionamento relativo do QR Code no template (em fração da largura/altura)
// Centralizado horizontalmente, posicionado COMPLETAMENTE ABAIXO do texto
// "Aponte a câmera para o QR Code" e das duas setas, dentro da moldura
// dourada reservada, sem sobrepor nenhum elemento.
const QR_CENTER_X_RATIO = 0.5 // 50% — centralizado horizontalmente
const QR_CENTER_Y_RATIO = 0.88 // ~88% — abaixo do texto e das setas
const QR_SIZE_RATIO = 0.15 // ~2,2cm em A5 — cabe dentro da moldura sem encostar nas bordas

async function gerarQrCodeDataUrl(
  link: string,
  tamanhoPx: number,
): Promise<string> {
  return await QRCode.toDataURL(link, {
    errorCorrectionLevel: 'M',
    margin: 4,
    width: tamanhoPx,
    color: {
      dark: '#2D6A4F',
      light: '#FFFFFF',
    },
  })
}

async function montarFolderCanvas(
  template: HTMLImageElement,
  usuario: string,
): Promise<HTMLCanvasElement> {
  const canvas = document.createElement('canvas')
  canvas.width = template.naturalWidth
  canvas.height = template.naturalHeight
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('Canvas não disponível.')

  ctx.drawImage(template, 0, 0, canvas.width, canvas.height)

  const link = `https://www.descubraseusoleos.com.br/?c=${usuario}`
  const qrSize = Math.round(canvas.width * QR_SIZE_RATIO)
  const qrDataUrl = await gerarQrCodeDataUrl(link, qrSize)
  const qrImg = await carregarImagem(qrDataUrl)

  const cx = canvas.width * QR_CENTER_X_RATIO
  const cy = canvas.height * QR_CENTER_Y_RATIO
  const drawX = Math.round(cx - qrSize / 2)
  const drawY = Math.round(cy - qrSize / 2)

  ctx.drawImage(qrImg, drawX, drawY, qrSize, qrSize)

  return canvas
}

export default function FolderPersonalizado({
  usuarioConsultora,
}: FolderPersonalizadoProps) {
  const previewRef = useRef<HTMLCanvasElement | null>(null)
  const [carregando, setCarregando] = useState(true)
  const [erroPreview, setErroPreview] = useState(false)
  const [baixandoPdf, setBaixandoPdf] = useState(false)
  const [baixandoPng, setBaixandoPng] = useState(false)

  useEffect(() => {
    let ativo = true
    setCarregando(true)
    setErroPreview(false)
    ;(async () => {
      try {
        const template = await carregarImagem(FOLDER_TEMPLATE_URL)
        const canvas = await montarFolderCanvas(template, usuarioConsultora)
        if (!ativo) return
        const ctx = previewRef.current?.getContext('2d')
        if (previewRef.current && ctx) {
          previewRef.current.width = canvas.width
          previewRef.current.height = canvas.height
          ctx.drawImage(canvas, 0, 0)
        }
      } catch (e) {
        console.error('Erro ao gerar pré-visualização do folder:', e)
        if (ativo) setErroPreview(true)
      } finally {
        if (ativo) setCarregando(false)
      }
    })()
    return () => {
      ativo = false
    }
  }, [usuarioConsultora])

  async function gerarCanvasFinal(): Promise<HTMLCanvasElement> {
    const template = await carregarImagem(FOLDER_TEMPLATE_URL)
    return await montarFolderCanvas(template, usuarioConsultora)
  }

  async function baixarPng() {
    if (baixandoPng) return
    setBaixandoPng(true)
    try {
      const canvas = await gerarCanvasFinal()
      try {
        canvas.toBlob(
          blob => {
            if (!blob) {
              toast.error('Erro ao gerar PNG.')
              return
            }
            const url = URL.createObjectURL(blob)
            const a = document.createElement('a')
            a.href = url
            a.download = `folder-diagnostico-${usuarioConsultora}.png`
            document.body.appendChild(a)
            a.click()
            document.body.removeChild(a)
            setTimeout(() => URL.revokeObjectURL(url), 10000)
            toast.success('Folder PNG baixado!')
          },
          'image/png',
          1.0,
        )
      } catch (corsErr) {
        console.error('Canvas bloqueado por restrição de segurança da imagem:', corsErr)
        toast.error('Não foi possível gerar o PNG agora. Atualize a página e tente novamente.')
      }
    } catch (e) {
      console.error(e)
      toast.error('Erro ao gerar o folder em PNG.')
    } finally {
      setBaixandoPng(false)
    }
  }

  async function baixarPdf() {
    if (baixandoPdf) return
    setBaixandoPdf(true)
    try {
      const canvas = await gerarCanvasFinal()
      let dataUrl: string
      try {
        dataUrl = canvas.toDataURL('image/jpeg', 0.95)
      } catch (corsErr) {
        console.error('Canvas bloqueado por restrição de segurança da imagem:', corsErr)
        toast.error('Não foi possível gerar o PDF agora. Atualize a página e tente novamente.')
        return
      }

      // A5 em mm: 148 x 210 (retrato)
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a5',
        compress: true,
      })

      pdf.addImage(dataUrl, 'JPEG', 0, 0, 148, 210, undefined, 'FAST')
      pdf.save(`folder-diagnostico-${usuarioConsultora}.pdf`)
      toast.success('Folder PDF baixado!')
    } catch (e) {
      console.error(e)
      toast.error('Erro ao gerar o folder em PDF.')
    } finally {
      setBaixandoPdf(false)
    }
  }

  return (
    <div>
      <div className="mb-4 bg-[#D8F3DC] border border-[#A8DFC4] rounded-xl px-4 py-3 text-sm text-verde-escuro leading-relaxed">
        📋 O QR Code inserido neste folder é do <strong>Diagnóstico de Saúde e Emoções (adulto)</strong>. Use-o para divulgar o diagnóstico em locais públicos como clínicas, consultórios, salões de beleza e comércio.
      </div>

      <p className="text-sm text-texto-escuro leading-relaxed mb-5">
        Baixe seu folder com seu QR Code já incluído. Imprima e deixe em
        clínicas, consultórios, salões de beleza, comércio e onde mais quiser
        divulgar.
      </p>

      {/* Pré-visualização */}
      <div className="mb-5 flex justify-center">
        <div
          className="relative bg-bege-fundo border-2 border-bege-medio rounded-2xl overflow-hidden shadow-md"
          style={{ width: 280, aspectRatio: '148 / 210' }}
        >
          {carregando && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-verde-escuro">
              <Loader2 className="w-6 h-6 animate-spin" />
              <span className="text-xs">Gerando pré-visualização...</span>
            </div>
          )}
          {erroPreview && !carregando && (
            <div className="absolute inset-0 flex items-center justify-center text-xs text-red-600 px-4 text-center">
              Não foi possível carregar a pré-visualização. Tente baixar
              diretamente nos botões abaixo.
            </div>
          )}
          <canvas
            ref={previewRef}
            className="w-full h-full block"
            style={{ display: carregando || erroPreview ? 'none' : 'block' }}
          />
        </div>
      </div>

      {/* Botões */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <button
          onClick={baixarPdf}
          disabled={baixandoPdf || carregando}
          className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl font-medium text-sm bg-verde-escuro text-white hover:bg-opacity-90 transition-all min-h-[48px] disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {baixandoPdf ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <FileText className="w-4 h-4" />
          )}
          {baixandoPdf ? 'Gerando PDF...' : 'Baixar meu folder em PDF'}
        </button>

        <button
          onClick={baixarPng}
          disabled={baixandoPng || carregando}
          className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl font-medium text-sm bg-white text-verde-escuro border-2 border-verde-escuro hover:bg-verde-muito-claro transition-all min-h-[48px] disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {baixandoPng ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Download className="w-4 h-4" />
          )}
          {baixandoPng ? 'Gerando PNG...' : 'Baixar meu folder em PNG'}
        </button>
      </div>

      <div className="mt-4 bg-[#FFF7ED] border border-[#FED7AA] rounded-xl px-4 py-3 text-sm text-[#92400E] leading-relaxed">
        💡 O <strong>PDF</strong> é ideal para impressão em alta qualidade
        (formato A4). O <strong>PNG</strong> é ideal para compartilhar
        digitalmente em redes sociais e WhatsApp.
      </div>
    </div>
  )
}
